import { ReactNode } from 'react';
import { NoteItemType } from '@act/shared/data-transfer-objects';
import { IconLibrary } from '@act/storybook';
import { Fade, Stack, Typography } from '@mui/material';

export type SaveStatus = 'IDLE' | 'SAVING' | 'SAVED' | 'ERROR';

interface SaveStatusIndicatorProps {
  status: SaveStatus;
  noteType?: NoteItemType;
}

const Row = ({ children }: { children: ReactNode }) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    {children}
  </Stack>
);

const getSaveText = (noteType: NoteItemType) => {
  switch (noteType) {
    case 'call':
      return 'Save & Close';
    case 'scheduled_event':
      return 'Schedule Event';
    case 'assigned_survey':
      return 'Save and close';
    default:
      return 'Save';
  }
};

const SaveStatusIndicator = ({
  status,
  noteType,
}: SaveStatusIndicatorProps) => {
  if (status === 'IDLE') return null;

  if (status === 'SAVING') {
    return (
      <span>
        <Row>
          <IconLibrary
            iconName="ArrowsCounterClockwise"
            size={16}
            weight="regular"
          />
          <Typography variant="bodySmall">Saving in progress</Typography>
        </Row>
      </span>
    );
  }

  if (status === 'ERROR') {
    return (
      <Row>
        <IconLibrary iconName="X" size={16} weight="light" color="error" />
        <Typography variant="bodySmall" color="error">
          Not saved · tap "{getSaveText(noteType)}"
        </Typography>
      </Row>
    );
  }

  // SAVED — fades in, then the parent clears it back to IDLE shortly after.
  return (
    <Fade in timeout={400}>
      <span>
        <Row>
          <IconLibrary
            iconName="Check"
            size={16}
            weight="light"
            color="success"
          />
          <Typography variant="bodySmall" color="green">
            Saved
          </Typography>
        </Row>
      </span>
    </Fade>
  );
};

export default SaveStatusIndicator;
